import { getMaImageLibrarySubjects, getMaImages, IMAGE_LIBRARY_UNUSED_SUBJECT, type MaImage } from '@/lib/zybooks-api';
import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';

const PAGE_SIZE = 50;
const EMPTY_SUBJECTS: string[] = [];

export { EMPTY_SUBJECTS as EMPTY_IMAGE_LIBRARY_SUBJECTS };

export interface UseImageLibraryListingArgs {
    enabled?: boolean;
    defaultOnlyUnused?: boolean;
    defaultSelectedSubjects?: string[];
}

export interface ImageLibraryListingState {
    allImages: MaImage[];
    setAllImages: Dispatch<SetStateAction<MaImage[]>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
    totalCount: number;
    loadingImages: boolean;
    loadingMore: boolean;
    showError: boolean;
    hasMore: boolean;
    loadMore: () => void;
    sortBy: 'date' | 'name';
    setSortBy: Dispatch<SetStateAction<'date' | 'name'>>;
    isDescending: boolean;
    setIsDescending: Dispatch<SetStateAction<boolean>>;
    subjectOptions: string[];
    selectedSubjects: string[];
    setSelectedSubjects: Dispatch<SetStateAction<string[]>>;
    disabledSubjects: string[];
    toggleSubjectEnabled: (subject: string) => void;
    onlyUnused: boolean;
    setOnlyUnused: Dispatch<SetStateAction<boolean>>;
    onlyMyUploads: boolean;
    setOnlyMyUploads: Dispatch<SetStateAction<boolean>>;
    displayedImages: MaImage[];
    resetAllSearchAndFilters: () => void;
}

function resolveImageLibraryQueryFilters(
    onlyUnused: boolean,
    selectedSubjects: string[],
    disabledSubjects: string[],
): { subjects?: string[]; excludeUnused?: boolean } {
    if (onlyUnused) {
        return { subjects: [IMAGE_LIBRARY_UNUSED_SUBJECT] };
    }
    const activeSubjects = selectedSubjects.filter(subject => !disabledSubjects.includes(subject));
    if (activeSubjects.length > 0) {
        return { subjects: activeSubjects };
    }
    return { excludeUnused: true };
}

export function useImageLibraryListing({
    enabled = true,
    defaultOnlyUnused = true,
    defaultSelectedSubjects = EMPTY_SUBJECTS,
}: UseImageLibraryListingArgs = {}): ImageLibraryListingState {
    const defaultSelectedSubjectsKey = defaultSelectedSubjects.join('|');
    const resolvedDefaultSelectedSubjects = useMemo(() => defaultSelectedSubjects, [defaultSelectedSubjects]);

    const [allImages, setAllImages] = useState<MaImage[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loadingImages, setLoadingImages] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showError, setShowError] = useState(false);
    const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
    const [isDescending, setIsDescending] = useState(true);
    const [subjectOptions, setSubjectOptions] = useState<string[]>([]);
    const [selectedSubjects, setSelectedSubjects] = useState(resolvedDefaultSelectedSubjects);
    const [disabledSubjects, setDisabledSubjects] = useState<string[]>([]);
    const [onlyUnused, setOnlyUnused] = useState(defaultOnlyUnused);
    const [onlyMyUploads, setOnlyMyUploads] = useState(false);

    useEffect(() => {
        queueMicrotask(() => {
            setSelectedSubjects(resolvedDefaultSelectedSubjects);
            setDisabledSubjects([]);
            setOnlyUnused(defaultOnlyUnused);
        });
    }, [defaultOnlyUnused, defaultSelectedSubjectsKey, resolvedDefaultSelectedSubjects]);

    const toggleSubjectEnabled = useCallback((subject: string) => {
        setDisabledSubjects(prev => (prev.includes(subject) ? prev.filter(value => value !== subject) : [...prev, subject]));
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let cancelled = false;

        const fetchSubjectOptions = async () => {
            try {
                const subjects = await getMaImageLibrarySubjects();
                if (!cancelled) {
                    setSubjectOptions(subjects);
                }
            } catch (error) {
                console.error('Error fetching image library subjects:', error);
            }
        };

        void fetchSubjectOptions();
        return () => {
            cancelled = true;
        };
    }, [enabled]);

    const activeSubjects = useMemo(
        () => selectedSubjects.filter(subject => !disabledSubjects.includes(subject)),
        [disabledSubjects, selectedSubjects],
    );

    const apiSubjectsKey = onlyUnused
        ? IMAGE_LIBRARY_UNUSED_SUBJECT
        : activeSubjects.length > 0
          ? activeSubjects.join(',')
          : 'exclude-unused';

    const queryKey = useMemo(
        () => `${sortBy}|${isDescending}|${apiSubjectsKey}|${onlyMyUploads}`,
        [sortBy, isDescending, apiSubjectsKey, onlyMyUploads],
    );

    const imageLibraryQueryFilters = useMemo(
        () => resolveImageLibraryQueryFilters(onlyUnused, selectedSubjects, disabledSubjects),
        [disabledSubjects, onlyUnused, selectedSubjects],
    );

    const hasMore = offset < totalCount;

    useEffect(() => {
        if (!enabled) {
            queueMicrotask(() => {
                setLoadingImages(false);
                setShowError(false);
            });
            return;
        }

        let cancelled = false;
        queueMicrotask(() => {
            setLoadingImages(true);
            setLoadingMore(false);
            setShowError(false);
            setAllImages([]);
            setTotalCount(0);
            setOffset(0);
        });

        const fetchImages = async () => {
            try {
                const response = await getMaImages({
                    limit: PAGE_SIZE,
                    offset: 0,
                    search: undefined, // leaving if we do want to search eventually
                    sortBy,
                    isDescending,
                    ...imageLibraryQueryFilters,
                    uploadedByMe: onlyMyUploads,
                });
                if (cancelled) {
                    return;
                }
                setAllImages(response.ma_images);
                setTotalCount(response.total_count);
                setOffset(response.ma_images.length);
                setLoadingImages(false);
            } catch (error) {
                console.error('Error fetching images:', error);
                if (!cancelled) {
                    setLoadingImages(false);
                    setShowError(true);
                    setAllImages([]);
                    setTotalCount(0);
                    setOffset(0);
                }
            }
        };

        void fetchImages();
        return () => {
            cancelled = true;
        };
    }, [enabled, queryKey, sortBy, isDescending, imageLibraryQueryFilters, onlyMyUploads]);

    const loadMore = useCallback(async () => {
        if (!enabled || loadingImages || loadingMore || offset >= totalCount) {
            return;
        }

        setLoadingMore(true);
        try {
            const response = await getMaImages({
                limit: PAGE_SIZE,
                offset,
                search: undefined,
                sortBy,
                isDescending,
                ...imageLibraryQueryFilters,
                uploadedByMe: onlyMyUploads,
            });
            setAllImages(prev => {
                const existingIds = new Set(prev.map(img => img.ma_image_id));
                const newImages = response.ma_images.filter(img => !existingIds.has(img.ma_image_id));
                return [...prev, ...newImages];
            });
            setTotalCount(response.total_count);
            setOffset(prev => prev + response.ma_images.length);
        } catch (error) {
            console.error('Error loading more images:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [enabled, loadingImages, loadingMore, offset, totalCount, sortBy, isDescending, imageLibraryQueryFilters, onlyMyUploads]);

    const resetAllSearchAndFilters = useCallback(() => {
        setSortBy('date');
        setIsDescending(true);
        setSelectedSubjects(resolvedDefaultSelectedSubjects);
        setDisabledSubjects([]);
        setOnlyUnused(defaultOnlyUnused);
        setOnlyMyUploads(false);
    }, [defaultOnlyUnused, resolvedDefaultSelectedSubjects]);

    return {
        allImages,
        setAllImages,
        setTotalCount,
        totalCount,
        loadingImages,
        loadingMore,
        showError,
        hasMore,
        loadMore,
        sortBy,
        setSortBy,
        isDescending,
        setIsDescending,
        subjectOptions,
        selectedSubjects,
        setSelectedSubjects,
        disabledSubjects,
        toggleSubjectEnabled,
        onlyUnused,
        setOnlyUnused,
        onlyMyUploads,
        setOnlyMyUploads,
        displayedImages: allImages,
        resetAllSearchAndFilters,
    };
}
